import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef, FilterFn } from '@tanstack/react-table';

import { ActivityDomainDtoIn } from '@/models/in/ActivityDomainDtoIn';
import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import EditOrCreateActivityDomainDialog from './EditOrCreateActivityDomainDialog';
import DeleteActivityDomainDialog from './DeleteActivityDomainDialog';

const QUERY_URL = 'management/activity-domains';

const activityDomainSearch: FilterFn<ActivityDomainDtoIn> = (row, _columnId, value) => {
  if (!value) return true;
  return row.original.name?.toLowerCase().includes(String(value).toLowerCase()) ?? false;
};

/**
 * Admin CRUD for the closed list of supplier activity domains (תחומי פעילות) that the
 * coordinator supplier forms offer. Deleting archives the domain: suppliers already
 * referencing it keep resolving its name, it just stops being offered.
 */
const ActivityDomainsManagementPage = () => {
  const { t } = useTranslation();

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [domainToEdit, setDomainToEdit] = useState<ActivityDomainDtoIn | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [domainToDelete, setDomainToDelete] = useState<ActivityDomainDtoIn | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: domains, isLoading, refetch } = useGetAuthApi<ActivityDomainDtoIn[]>(QUERY_URL);

  const handleOpenCreateDialog = useCallback(() => {
    setDomainToEdit(null);
    setIsEditDialogOpen(true);
  }, []);

  const handleOpenEditDialog = useCallback((domain: ActivityDomainDtoIn) => {
    setDomainToEdit(domain);
    setIsEditDialogOpen(true);
  }, []);

  const handleCloseEditDialog = useCallback(() => {
    setIsEditDialogOpen(false);
    setDomainToEdit(null);
  }, []);

  const handleOpenDeleteDialog = useCallback((domain: ActivityDomainDtoIn) => {
    setDomainToDelete(domain);
    setIsDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setDomainToDelete(null);
  }, []);

  const columns: ColumnDef<ActivityDomainDtoIn>[] = [
    {
      id: 'name',
      header: Col(t('activityDomainsManagement.name')),
      accessorKey: 'name',
      enableSorting: true,
      meta: {
        className: 'min-w-[250px]',
      },
    },
    {
      id: 'actions',
      header: Col(t('activityDomainsManagement.actions')),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEditDialog(row.original);
            }}
          >
            {t('activityDomainsManagement.edit')}
          </Button>
          <Button
            variant="outline"
            className="!text-red-500 !border-red-300"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenDeleteDialog(row.original);
            }}
          >
            {t('activityDomainsManagement.delete')}
          </Button>
        </div>
      ),
      meta: {
        className: 'min-w-[200px]',
      },
    },
  ];

  return (
    <div>
      <DataTable
        title={t('activityDomainsManagement.title')}
        data={domains ?? []}
        isLoading={isLoading}
        columns={columns}
        filters={
          <div className="flex items-center justify-between gap-3">
            <Button variant="primary" onClick={handleOpenCreateDialog}>
              {t('activityDomainsManagement.addDomain')}
            </Button>
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="activityDomainsManagement.search"
            />
          </div>
        }
        emptyStateContent={{ title: t('activityDomainsManagement.noData'), description: t('activityDomainsManagement.noDataDescription') }}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customGlobalSearch={activityDomainSearch}
      />

      <EditOrCreateActivityDomainDialog
        open={isEditDialogOpen}
        onClose={handleCloseEditDialog}
        domainToEdit={domainToEdit}
        onSuccess={refetch}
      />

      {isDeleteDialogOpen && domainToDelete && (
        <DeleteActivityDomainDialog
          open={isDeleteDialogOpen}
          onClose={handleCloseDeleteDialog}
          domain={domainToDelete}
          onSuccess={refetch}
        />
      )}
    </div>
  );
};

export default ActivityDomainsManagementPage;
